import { PhoneOff, Timer, Search, Star, Globe, Bot, MapPin, Check, ArrowRight, Share2, Megaphone } from "lucide-react";
import { SectionHeader } from "@/components/landing/shared";
import Marquee from "@/components/landing/Marquee";
import AdsThatWin from "@/components/landing/AdsThatWin";
import Receipts from "@/components/landing/Receipts";
import SiteApp from "./SiteApp";
import SiteNiches from "./SiteNiches";
import { AeoExplainer } from "./AeoExplainer";
import { useSiteBook } from "./SiteBookModal";
import { SiteCTA } from "./shared";
import { PricingCards } from "./PricingCards";

const PROBLEMS = [
  { icon: PhoneOff, title: "You miss the call. They call the next guy.", desc: "Most people who hit your voicemail never call back. They just dial the next business on the list." },
  { icon: Timer, title: "Slow replies lose clients.", desc: "Answer within 5 minutes and you're 8x more likely to win the client. Most local businesses take hours." },
  { icon: Search, title: "You don't show up on Google.", desc: "When someone nearby searches what you do, they pick from the first 3 results. If that's not you, you're invisible." },
  { icon: Star, title: "Your reviews went quiet.", desc: "A competitor with 40 fresh reviews beats you with 200 old ones. Google ranks businesses that keep them coming." },
];

const PILLARS = [
  { icon: MapPin, title: "A Google profile that pulls calls", desc: "We tune up your Google Business Profile and turn your past clients into fresh 5-star reviews — so you climb local search and the map pack.", points: ["Full Google profile tune-up", "We text your past clients a review link for you", "Every review gets a reply — good or bad"] },
  { icon: Globe, title: "A website that books clients", desc: "Fast, clean, and built to do one thing: turn visitors into booked clients. Every page makes it dead simple to call you or book online.", points: ["Built for you, start to finish", "Online booking + click-to-call", "A page for every service you offer"] },
  { icon: Bot, title: "An AI helper that never sleeps", desc: "Every new lead gets a text back in 60 seconds — nights, weekends, whenever. It answers questions and books them straight onto your calendar.", points: ["60-second text-back, 24/7", "Missed calls get a text automatically", "Books clients while you're with one"] },
  { icon: Search, title: "SEO that keeps compounding", desc: "A page for every service and every area you cover, so you show up when locals search — and keep getting those leads month after month without paying per click.", points: ["Service + city pages that rank", "Free leads long after launch", "Your cost per lead drops monthly"] },
  { icon: Share2, title: "Social media, handled", desc: "We post for you on Meta, your Google Business Profile, and YouTube — so when clients check you out before calling, you look alive and trusted everywhere.", points: ["Posts written + published for you", "Meta, Google & YouTube covered", "Win the trust vote before they call"] },
  { icon: Megaphone, title: "Done-for-you paid ads", desc: "When your foundation is converting, we scale you with Meta & Google ads — 20+ fresh creatives and copy every month, strategy, testing, and budgets all handled.", points: ["Meta + Google campaigns managed", "20+ new ads monthly", "Weekly testing — kill losers, scale winners"] },
];

const STEPS = [
  { n: "1", title: "Book a free onboarding call", desc: "Fifteen minutes. We look at your business, show you what you're losing, and answer every question — no pressure, no jargon." },
  { n: "2", title: "We build everything", desc: "Website, AI helper, Google tune-up — all done for you and live in about a week. You don't touch a thing." },
  { n: "3", title: "Clients come in", desc: "Every lead answered in seconds, every booking on your calendar, your name climbing Google. You just do the work." },
];

const FAQS = [
  { q: "What do I actually get?", a: "The full marketing system: a Google profile tune-up, a website built to book clients, and an AI helper that answers every lead in 60 seconds — then SEO, social media posting, and done-for-you Meta & Google ads as you're ready to scale. One team runs all of it." },
  { q: "Do you run paid ads?", a: "Yes — Meta and Google ads are a core part of what we do, with 20+ fresh ad creatives and copy every month. We just don't start there: ads only pay once your foundation (Google profile, website, follow-up) actually converts the clicks. When we do turn spend on, every dollar lands on a machine built to book the client." },
  { q: "Is there a contract?", a: "No. Month to month, cancel anytime. No setup fees, no cancellation fees." },
  { q: "I already have a website. Now what?", a: "Keep it if you love it — or use the one we build. Either way the AI helper, Google setup, SEO, and ads all work with it." },
  { q: "What does the AI actually do?", a: "The moment a lead comes in — form, call, or message — it texts them back within 60 seconds, answers their questions, and books them onto your calendar. If you miss a call, it texts them automatically so they don't call your competitor." },
  { q: "How fast is this live?", a: "The foundation (website, AI, Google profile) is usually live within about a week of your first call. SEO and social ramp from month one, and ads switch on when the numbers say you're ready." },
  { q: "Do I own my leads and contacts?", a: "Yes. They're yours. Export them anytime — and if you ever cancel, we hand you a full export. No hostage games." },
];

const FAQBlock = ({ q, a }) => (
  <details className="group rounded-2xl border border-navy/8 bg-white px-5 py-4 open:border-brand-indigo/25">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-bold text-navy [&::-webkit-details-marker]:hidden">
      {q}
      <span className="text-brand-indigo transition-transform group-open:rotate-45">+</span>
    </summary>
    <p className="mt-3 text-sm leading-relaxed text-navy/60">{a}</p>
  </details>
);

const HERO_STATS = [
  ["60 sec", "average text-back to every new lead"],
  ["24/7", "nights, weekends, holidays — covered"],
  ["20+", "fresh ads every month when you scale"],
];

export default function SiteHome() {
  const { openBook } = useSiteBook();
  return (
    <>
      {/* ============ HERO (centered, Skye-style with the Slammed twist) ============ */}
      <section data-testid="site-hero" className="relative overflow-hidden bg-cream/60 px-5 pb-16 pt-24 text-center sm:px-8 sm:pt-32">
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-indigo/15 blur-3xl" />
        <div className="pointer-events-none absolute top-20 -right-24 h-96 w-96 rounded-full bg-brand-sky/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-1/3 h-80 w-80 rounded-full bg-brand-violet/10 blur-3xl" />
        <div className="relative mx-auto max-w-4xl">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/40 lg:text-[11px]" data-testid="hero-eyebrow">
            The growth system for local practices &amp; businesses
          </p>
          <h1 className="mt-5 font-display text-[2.6rem] font-extrabold leading-[1.04] tracking-tight text-navy sm:text-6xl lg:text-7xl">
            The Booked-Client Engine
            <br />
            <span className="text-gradient inline-block" data-testid="hero-rotating-trade">Built for Local Businesses.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
            We get you found on Google. Our AI answers every lead in 60 seconds.
            Our team runs the ads, the SEO, and the social. You just do the work.
          </p>
          <div className="mx-auto mt-8 flex flex-col items-center gap-3">
            <SiteCTA testId="hero-book-cta" className="px-8 py-4 text-base">Book a free call</SiteCTA>
            <p className="text-[12.5px] font-semibold text-navy/45" data-testid="hero-cta-note">15 minutes · No card · No pressure</p>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-4">
            <a href="#results" data-testid="hero-results-cta" className="group inline-flex items-center gap-1.5 text-[15px] font-bold text-brand-indigo">
              See real results <ArrowRight className="h-4 w-4 rotate-90 transition-transform group-hover:translate-y-0.5" />
            </a>
          </div>
          <p className="mt-3.5 text-[13px] font-semibold text-navy/50" data-testid="hero-deliverable-line">
            Free competitor report + website — built before your call, revealed on it. Yours to keep either way.
          </p>
          <p className="mt-6 text-[13px] font-semibold text-navy/45">
            Month to month · Live in about a week · Cancel anytime
          </p>
          <div className="mx-auto mt-12 grid max-w-2xl grid-cols-3 gap-4 border-t border-navy/8 pt-8" data-testid="hero-stats">
            {HERO_STATS.map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-extrabold tracking-tight text-gradient sm:text-4xl">{n}</div>
                <div className="mt-1.5 text-[11.5px] font-semibold leading-snug text-navy/50">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* ============ PROBLEM ============ */}
      <section data-testid="site-problem" className="py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHeader
            eyebrow="The real problem"
            title={<>You're losing clients you <span className="text-gradient">already won the search for.</span></>}
            subtitle="It's not that you need more marketing. It's that the leads you already get slip through the cracks."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            {PROBLEMS.map((p) => (
              <div key={p.title} className="rounded-2xl border border-navy/8 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(14,18,48,0.15)]">
                <p.icon className="h-6 w-6 text-brand-indigo" />
                <h3 className="mt-4 font-display text-lg font-extrabold tracking-tight text-navy">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-navy/55">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PILLARS ============ */}
      <section data-testid="site-pillars" className="bg-cream/60 py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHeader
            align="center"
            eyebrow="What we do"
            title={<>The whole marketing department. <span className="text-gradient">Done for you.</span></>}
            subtitle="Six services, one team, one system — built in the order that makes each one pay for the next."
          />
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-3xl border border-navy/[0.06] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-18px_rgba(14,18,48,0.18)]">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-indigo/10">
                  <p.icon className="h-5.5 w-5.5 text-brand-indigo" />
                </span>
                <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight text-navy">{p.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/55">{p.desc}</p>
                <ul className="mt-4 space-y-2 border-t border-navy/8 pt-4">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-[13px] font-semibold text-navy/70">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />{pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <SiteCTA href="/solutions" testId="pillars-more">See everything that's included</SiteCTA>
          </div>
        </div>
      </section>

      {/* ============ AEO EXPLAINER (moved from /getleads per owner) ============ */}
      <AeoExplainer onOpen={openBook} label="Book my free strategy call" />

      <SiteApp />

      {/* ============ SHOWCASE: ad creatives + campaign results carousels ============ */}
      <AdsThatWin ctaOnClick={openBook} ctaLabel="Book a free call" />
      <Receipts />

      {/* ============ STRATEGY CALL BANNER ============ */}
      <section data-testid="site-strategy-banner" className="py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-navy px-7 py-12 text-center sm:px-12">
            <div className="pointer-events-none absolute -top-24 left-1/4 h-64 w-64 rounded-full bg-brand-indigo/25 blur-3xl" />
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Want us to map all of this for your business?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-white/60">
              Book a free market strategy call. We build your competitor report and your new
              website before it — then walk you through both on the call. You keep them either way, whether you hire us or not.
            </p>
            <div className="mt-7">
              <button type="button" onClick={openBook} data-testid="banner-strategy-cta"
                className="group inline-flex cursor-pointer items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display text-[15px] font-bold text-navy transition-transform duration-300 hover:-translate-y-1">
                Book my free strategy call <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section data-testid="site-how" className="bg-cream/60 py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHeader
            align="center"
            eyebrow="How it works"
            title={<>Live in about a week. <span className="text-gradient">Seriously.</span></>}
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-3xl border border-navy/[0.06] bg-white p-7">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gradient font-display text-lg font-extrabold text-white">{s.n}</span>
                <h3 className="mt-5 font-display text-lg font-extrabold tracking-tight text-navy">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-navy/55">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <a href="/how-it-works" data-testid="home-phases-link" className="group inline-flex items-center gap-1.5 text-sm font-bold text-brand-indigo">
              See the full 3-phase roadmap <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </div>
      </section>

      <SiteNiches />

      {/* ============ PRICING ============ */}
      <section id="pricing" data-testid="site-pricing" className="scroll-mt-24 bg-cream/60 py-20 sm:py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <SectionHeader
            align="center"
            eyebrow="Pricing"
            title={<>Simple plans. <span className="text-gradient">Booked customers.</span></>}
            subtitle="Month to month, cancel anytime — no contracts, no setup fees. Start right now with secure checkout, or book a free competitor analysis call first."
          />
          <div className="mt-12">
            <PricingCards />
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-[13px] leading-relaxed text-navy/50">
            Month to month. Cancel anytime. You keep your leads, your profile, and your reviews either way.
          </p>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section data-testid="site-faq" className="py-20 sm:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <SectionHeader align="center" eyebrow="Questions" title={<>Fair questions. <span className="text-gradient">Straight answers.</span></>} />
          <div className="mt-10 space-y-3">
            {FAQS.map((f) => <FAQBlock key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ============ FINAL: booking CTA (pops the funnel modal) ============ */}
      <section id="book" data-testid="site-book-section" className="scroll-mt-24 pb-24 pt-4">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-brand-gradient px-7 py-14 text-center sm:px-12">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Stop losing clients to slower competitors.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-[15px] text-white/75">
              Book your free market strategy call — fifteen minutes, plain English, with your competitor report and website already built. Yours to keep either way.
            </p>
            <div className="mt-8">
              <button type="button" onClick={openBook} data-testid="final-book-call"
                className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-white px-8 py-4 font-display text-base font-bold text-navy transition-transform duration-300 hover:-translate-y-1">
                Book a free call <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
